import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel } from '../../api/HealthSystemApi';

interface CreateUpdateSlaughterhouseModalProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
  mode: 'create' | 'update';
  slaughterhouse: HealthSystemDomainSlaughterhouseModelsSlaughterhouseViewModel | null;
}

const CreateUpdateSlaughterhouseModal: React.FC<CreateUpdateSlaughterhouseModalProps> = ({
  opened,
  onModalAction,
  mode,
  slaughterhouse
}) => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [name, setName] = React.useState(slaughterhouse?.name || '');
  const [code, setCode] = React.useState(slaughterhouse?.code || '');
  const [address, setAddress] = React.useState(slaughterhouse?.address || '');
  const [contactEmail, setContactEmail] = React.useState(slaughterhouse?.contactEmail || '');
  const [contactPhone, setContactPhone] = React.useState(slaughterhouse?.contactPhone || '');
  const [isActive, setIsActive] = React.useState(slaughterhouse?.isActive ?? true);

  const [isBusy, setIsBusy] = React.useState(false);

  React.useEffect(() => {
    if (slaughterhouse) {
      setName(slaughterhouse?.name || '');
      setCode(slaughterhouse?.code || '');
      setAddress(slaughterhouse?.address || '');
      setContactEmail(slaughterhouse?.contactEmail || '');
      setContactPhone(slaughterhouse?.contactPhone || '');
      setIsActive(slaughterhouse?.isActive ?? true);
    }
  }, [slaughterhouse]);

  const closeDialog = () => {
    clearFields();
    onModalAction(null);
    return;
  };

  const clearFields = () => {
    setName('');
    setCode('');
    setAddress('');
    setContactEmail('');
    setContactPhone('');
    setIsActive(true);
  };

  const validate = (): boolean => {
    if (!name || name.trim().length === 0) {
      notifications.error(t('f-val-slaughterhouse-name'));
      return false;
    }

    // Email validation if provided
    if (contactEmail && contactEmail.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactEmail)) {
        notifications.error(t('f-val-email'));
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    setIsBusy(true);

    if (mode === 'create') {
      apiService.api.slaughterhouses
        .slaughterhousesCreate({
          name,
          code: code || null,
          address: address || null,
          contactEmail: contactEmail || null,
          contactPhone: contactPhone || null
        })
        .then(() => {
          notifications.success(t('f-slaughterhouse-created'));
          clearFields();
          onModalAction(true);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    } else {
      if (!slaughterhouse?.id) {
        notifications.error(t('f-com-api-error'));
        setIsBusy(false);
        return;
      }

      apiService.api.slaughterhouses
        .slaughterhousesUpdate(
          { id: slaughterhouse.id },
          {
            name: name || null,
            code: code || null,
            address: address || null,
            contactEmail: contactEmail || null,
            contactPhone: contactPhone || null,
            isActive: isActive
          }
        )
        .then(() => {
          notifications.success(t('f-slaughterhouse-updated'));
          clearFields();
          onModalAction(true);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };

  return (
    <Dialog open={opened} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container direction="row" alignItems="center">
          <Grid item>
            <Icon color="primary">factory</Icon>
          </Grid>
          <Grid item className={classes.ml8}>
            {mode === 'create' ? t('f-slaughterhouse-create') : t('f-slaughterhouse-update')}
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label={t('f-slaughterhouse-name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isBusy}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('f-slaughterhouse-code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isBusy}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('f-slaughterhouse-address')}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={isBusy}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('f-slaughterhouse-contact-email')}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              disabled={isBusy}
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('f-slaughterhouse-contact-phone')}
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              disabled={isBusy}
            />
          </Grid>
          {mode === 'update' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                    disabled={isBusy}
                  />
                }
                label={t('f-com-active')}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} disabled={isBusy}>
          {t('f-com-cancel')}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={isBusy}>
          {isBusy ? <CircularProgress size={24} /> : t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateSlaughterhouseModal;
